import React, { useMemo, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { createAppTheme } from './theme';
import { useThemeStore, themePalettes } from './stores/themeStore';
import { useAuthStore } from './stores/authStore';
import { useSettingsStore } from './stores/settingsStore';
import { useComposeTabsStore } from './stores/composeTabsStore';
import { BrandingProvider } from './utils/BrandingContext';
import { isHelpdeskMode, isCustomDomain } from './utils/helpdeskMode';
import { MailLayout } from './components/layout';
import HybridMailLayout from './components/layout/HybridMailLayout';
import { requestNotificationPermission } from './services/notificationService';
import { EmailListView } from './components/mail';
import { usePortalAuthStore } from './stores/portalAuthStore';

// Keep only auth pages as static imports (needed immediately on login page)
import { Login, ForgotPassword, SignInForm, ResetPassword } from './pages/auth';

// LAZY LOAD all other pages - they load on-demand when route is accessed
// Marketing & Info Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'));
const MigrationPage = lazy(() => import('./pages/MigrationPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));

// Legal Pages
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const AcceptableUsePolicyPage = lazy(() => import('./pages/AcceptableUsePolicyPage'));
const DataProcessingAgreementPage = lazy(() => import('./pages/DataProcessingAgreementPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const SLAPage = lazy(() => import('./pages/SLAPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));

// Industry Pages
const ShieldPage = lazy(() => import('./pages/ShieldPage'));
const SmallBusinessPage = lazy(() => import('./pages/SmallBusinessPage'));
const StartupsPage = lazy(() => import('./pages/StartupsPage'));
const HealthcarePage = lazy(() => import('./pages/HealthcarePage'));
const EducationPage = lazy(() => import('./pages/EducationPage'));
const NonProfitPage = lazy(() => import('./pages/NonProfitPage'));

// App Features
const AIAssistant = lazy(() => import('./pages/ai').then(m => ({ default: m.AIAssistant })));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));
const TasksPage = lazy(() => import('./pages/tasks/TasksPage'));
const ContactsPage = lazy(() => import('./pages/contacts/ContactsPage'));
const NotesPage = lazy(() => import('./pages/notes/NotesPage'));
const StreamsPage = lazy(() => import('./pages/streams').then(m => ({ default: m.StreamsPage })));
const BookmarksPage = lazy(() => import('./pages/bookmarks').then(m => ({ default: m.BookmarksPage })));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));
const DocumentEditor = lazy(() => import('./pages/docs/DocumentEditor'));
const AttachmentEditor = lazy(() => import('./pages/docs/AttachmentEditor'));
const DrivexPage = lazy(() => import('./pages/drivex/DrivexPage'));
const ArchivedMailPage = lazy(() => import('./pages/ArchivedMailPage'));

// Helpdesk
const HelpdeskLayout = lazy(() => import('./components/helpdesk/HelpdeskLayout'));
const HelpdeskDashboard = lazy(() => import('./pages/helpdesk/HelpdeskDashboard'));
const HelpdeskTickets = lazy(() => import('./pages/helpdesk/HelpdeskTickets'));
const HelpdeskTicketDetail = lazy(() => import('./pages/helpdesk/HelpdeskTicketDetail'));
const HelpdeskAgentQueue = lazy(() => import('./pages/helpdesk/HelpdeskAgentQueue'));
const HelpdeskCustomers = lazy(() => import('./pages/helpdesk/HelpdeskCustomers'));
const HelpdeskKnowledgeBase = lazy(() => import('./pages/helpdesk/HelpdeskKnowledgeBase'));
const HelpdeskReports = lazy(() => import('./pages/helpdesk/HelpdeskReports'));
const HelpdeskSettings = lazy(() => import('./pages/helpdesk/HelpdeskSettings'));
const HelpdeskPortalLogin = lazy(() => import('./pages/helpdesk/HelpdeskPortalLogin'));
const HelpdeskPortalTickets = lazy(() => import('./pages/helpdesk/HelpdeskPortalTickets'));
const HelpdeskPortalTicketDetail = lazy(() => import('./pages/helpdesk/HelpdeskPortalTicketDetail'));
const HelpdeskPortalForgotPassword = lazy(() => import('./pages/helpdesk/HelpdeskPortalForgotPassword'));
const HelpdeskPortalResetPassword = lazy(() => import('./pages/helpdesk/HelpdeskPortalResetPassword'));

// Analytics & Chat
const AnalyticsPage = lazy(() => import('./pages/analytics').then(m => ({ default: m.AnalyticsPage })));
const TeamChatPage = lazy(() => import('./components/chat/TeamChatPage'));

// Profile & Billing
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));
const PlansPage = lazy(() => import('./pages/billing/PlansPage'));
const CheckoutPage = lazy(() => import('./pages/billing/CheckoutPage'));

// Guide & Help
const GuidePage = lazy(() => import('./pages/guide/GuidePage'));
const HelpPage = lazy(() => import('./pages/help/HelpPage'));

// Setup Pages
const PlanSelection = lazy(() => import('./pages/setup').then(m => ({ default: m.PlanSelection })));
const OrganizationSignup = lazy(() => import('./pages/setup').then(m => ({ default: m.OrganizationSignup })));
const VerifyOTP = lazy(() => import('./pages/setup').then(m => ({ default: m.VerifyOTP })));
const DomainSetup = lazy(() => import('./pages/setup').then(m => ({ default: m.DomainSetup })));
const DomainVerification = lazy(() => import('./pages/setup').then(m => ({ default: m.DomainVerification })));
const AdminUserSetup = lazy(() => import('./pages/setup').then(m => ({ default: m.AdminUserSetup })));
const DNSSetup = lazy(() => import('./pages/setup').then(m => ({ default: m.DNSSetup })));
const AddUsersSetup = lazy(() => import('./pages/setup').then(m => ({ default: m.AddUsersSetup })));
const SetupComplete = lazy(() => import('./pages/setup').then(m => ({ default: m.SetupComplete })));

// Settings Pages
const SettingsLayout = lazy(() => import('./pages/settings').then(m => ({ default: m.SettingsLayout })));
const SystemSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.SystemSettings })));
const KeyboardShortcuts = lazy(() => import('./pages/settings').then(m => ({ default: m.KeyboardShortcuts })));
const IntegrationsSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.IntegrationsSettings })));
const NotificationSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.NotificationSettings })));
const ComposeSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.ComposeSettings })));
const SignatureSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.SignatureSettings })));
const OutOfOfficeSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.OutOfOfficeSettings })));
const FoldersSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.FoldersSettings })));
const LabelsSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.LabelsSettings })));
const AccountSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.AccountSettings })));
const MailAccountsSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.MailAccountsSettings })));
const AntiSpamSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.AntiSpamSettings })));
const FiltersSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.FiltersSettings })));
const ImportExportSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.ImportExportSettings })));
const MailViewOptions = lazy(() => import('./pages/settings').then(m => ({ default: m.MailViewOptions })));
const AppPasswordsSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.AppPasswordsSettings })));

// Loading fallback component shown while lazy components load
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress />
  </Box>
);

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, token } = useAuthStore();
  const location = useLocation();
  
  // Also check localStorage directly — the interceptor may have refreshed the token
  // before Zustand's persisted state rehydrates
  const hasToken = token || localStorage.getItem('accessToken');
  
  if (!isAuthenticated && !hasToken) {
    // Native Capacitor app or deeper routes → direct sign-in form; root path on web → landing page
    const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
    const target = (isNative || location.pathname !== '/') ? '/login/direct' : '/login';
    return <Navigate to={target} state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

// Portal Protected Route — requires portal user auth
const PortalProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = usePortalAuthStore();
  if (!isAuthenticated) return <Navigate to="/portal/login" replace />;
  return <>{children}</>;
};

// Helpdesk guard — redirects non-support routes to /support in helpdesk mode
const HelpdeskGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = window.location.pathname;
  if (isHelpdeskMode() && !location.startsWith('/support') && !location.startsWith('/login') && !location.startsWith('/forgot-password') && !location.startsWith('/reset-password') && !location.startsWith('/portal')) {
    return <Navigate to="/support" replace />;
  }
  return <>{children}</>;
};

// Redirect /compose to inbox and open compose as tab
const ComposeRedirect: React.FC = () => {
  const { getOrCreateTab } = useComposeTabsStore();
  useEffect(() => { getOrCreateTab(); }, []);
  return <Navigate to="/mail/inbox" replace />;
};

// Redirect /chat URL to open chat as tab inside mail layout
const ChatRedirect: React.FC = () => {
  const { openChat } = useComposeTabsStore();
  useEffect(() => { openChat(); }, []);
  return <Navigate to="/mail/inbox" replace />;
};

// Redirect /docs URL to open Documents as inner tab
const DocsRedirect: React.FC = () => {
  const { openDocs } = useComposeTabsStore();
  useEffect(() => { openDocs(); }, []);
  return <Navigate to="/mail/inbox" replace />;
};

// Redirect /sheets URL to open Spreadsheets as inner tab
const SheetsRedirect: React.FC = () => {
  const { openSheets } = useComposeTabsStore();
  useEffect(() => { openSheets(); }, []);
  return <Navigate to="/mail/inbox" replace />;
};

// Public Route wrapper (redirect if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to={isHelpdeskMode() ? '/support/dashboard' : '/mail/inbox'} replace />;
  }
  
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              {(window as any).Capacitor?.isNativePlatform?.()
                ? <Navigate to="/login/direct" replace />
                : isCustomDomain()
                  ? <Login />
                  : <LandingPage />}
            </PublicRoute>
          }
        />
        <Route
          path="/login/direct"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <PlanSelection />
            </PublicRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <PublicRoute>
              <PlanSelection />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <PlanSelection />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/signin-form"
          element={
            <PublicRoute>
              <SignInForm />
            </PublicRoute>
          }
        />
        <Route
          path="/contact"
          element={<ContactPage />}
        />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/enterprise" element={<EnterprisePage />} />
        <Route path="/migration" element={<MigrationPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/help-center" element={<HelpCenterPage />} />
        <Route path="/documentation" element={<DocumentationPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/acceptable-use-policy" element={<AcceptableUsePolicyPage />} />
        <Route path="/data-processing-agreement" element={<DataProcessingAgreementPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/sla" element={<SLAPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/shield" element={<ShieldPage />} />
        <Route path="/small-business" element={<SmallBusinessPage />} />
        <Route path="/startups" element={<StartupsPage />} />
        <Route path="/healthcare" element={<HealthcarePage />} />
        <Route path="/education" element={<EducationPage />} />
        <Route path="/nonprofit" element={<NonProfitPage />} />
        <Route path="/mail/help" element={<HelpPage />} />

        {/* Setup wizard routes */}
        <Route path="/setup" element={<PlanSelection />} />
        <Route path="/setup/plans" element={<PlanSelection />} />
        <Route path="/setup/organization" element={<OrganizationSignup />} />
        <Route path="/setup/verify" element={<VerifyOTP />} />
        <Route path="/setup/domain" element={<DomainSetup />} />
        <Route path="/setup/verify-domain" element={<DomainVerification />} />
        <Route path="/setup/admin-user" element={<AdminUserSetup />} />
        <Route path="/setup/dns" element={<DNSSetup />} />
        <Route path="/setup/users" element={<AddUsersSetup />} />
        <Route path="/setup/complete" element={<SetupComplete />} />

        {/* Document editor - full screen, outside MailLayout */}
        <Route
          path="/editor/:id"
          element={
            <ProtectedRoute>
              <DocumentEditor />
            </ProtectedRoute>
          }
        />

        {/* Attachment editor - full screen OnlyOffice for email attachments */}
        <Route
          path="/attachment-editor"
          element={
            <ProtectedRoute>
              <AttachmentEditor />
            </ProtectedRoute>
          }
        />

        {/* Portal routes — always accessible regardless of mode so end-customers can use mail.domain.com/portal/login */}
        <Route path="/portal/login" element={<HelpdeskPortalLogin />} />
        <Route path="/portal/forgot-password" element={<HelpdeskPortalForgotPassword />} />
        <Route path="/portal/reset-password" element={<HelpdeskPortalResetPassword />} />
        <Route path="/portal/tickets" element={<PortalProtectedRoute><HelpdeskPortalTickets /></PortalProtectedRoute>} />
        <Route path="/portal/tickets/:id" element={<PortalProtectedRoute><HelpdeskPortalTicketDetail /></PortalProtectedRoute>} />

        {/* Helpdesk mode: dedicated layout and routes */}
        {isHelpdeskMode() && (
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HelpdeskLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/support/dashboard" replace />} />
            <Route path="support/dashboard" element={<HelpdeskDashboard />} />
            <Route path="support/tickets" element={<HelpdeskTickets />} />
            <Route path="support/tickets/:id" element={<HelpdeskTicketDetail />} />
            <Route path="support/queue" element={<HelpdeskAgentQueue />} />
            <Route path="support/customers" element={<HelpdeskCustomers />} />
            <Route path="support/knowledge" element={<HelpdeskKnowledgeBase />} />
            <Route path="support/reports" element={<HelpdeskReports />} />
            <Route path="support/settings" element={<HelpdeskSettings />} />
            <Route path="support" element={<Navigate to="/support/dashboard" replace />} />
            <Route path="support/:id" element={<HelpdeskTicketDetail />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="*" element={<Navigate to="/support/dashboard" replace />} />
          </Route>
        )}

        {/* Normal webmail mode */}
        {!isHelpdeskMode() && (
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HybridMailLayout />
            </ProtectedRoute>
          }
        >
            <>
              {/* Mail routes */}
              <Route index element={<Navigate to="/mail/inbox" replace />} />
              <Route path="mail" element={<Navigate to="/mail/inbox" replace />} />
              <Route path="mail/guide" element={<GuidePage />} />
              <Route path="mail/archived" element={<ArchivedMailPage />} />
              <Route path="mail/:folder" element={<EmailListView />} />
              <Route path="mail/:folder/:emailId" element={<EmailListView />} />
              
              {/* Compose route - redirects to inbox and opens compose tab */}
              <Route path="compose" element={<ComposeRedirect />} />

              {/* AI Assistant */}
              <Route path="ai" element={<AIAssistant />} />
              <Route path="ai-assistant" element={<AIAssistant />} />

              {/* Other app routes */}
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="notes" element={<NotesPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="streams" element={<StreamsPage />} />
              <Route path="bookmarks" element={<BookmarksPage />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="support/:ticketId" element={<SupportPage />} />
              <Route path="docs" element={<DocsRedirect />} />
              <Route path="sheets" element={<SheetsRedirect />} />
              <Route path="drivex" element={<DrivexPage />} />
              <Route path="drivex/folder/:folderId" element={<DrivexPage />} />
              <Route path="drivex/shared/:shareLink" element={<DrivexPage />} />
              <Route path="chat" element={<ChatRedirect />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="plans" element={<PlansPage />} />
              <Route path="checkout" element={<CheckoutPage />} />
            </>
        </Route>
        )}

        {/* Settings routes - rendered as modal overlay on top of mail (disabled in helpdesk mode) */}
        {!isHelpdeskMode() && (
          <Route
            path="/settings/*"
            element={
              <ProtectedRoute>
                <>
                  <HybridMailLayout />
                  <Routes>
                    <Route element={<SettingsLayout />}>
                      <Route index element={<Navigate to="/settings/system" replace />} />
                      <Route path="system" element={<SystemSettings />} />
                      <Route path="keyboard-shortcuts" element={<KeyboardShortcuts />} />
                      <Route path="integrations" element={<IntegrationsSettings />} />
                      <Route path="integrations/extensions" element={<IntegrationsSettings />} />
                      <Route path="integrations/developer" element={<IntegrationsSettings />} />
                      <Route path="integrations/channels" element={<IntegrationsSettings />} />
                      <Route path="preset" element={<AccountSettings />} />
                      <Route path="notification" element={<NotificationSettings />} />
                      <Route path="notification/desktop" element={<NotificationSettings />} />
                      <Route path="notification/priority" element={<NotificationSettings />} />
                      <Route path="notification/quiet" element={<NotificationSettings />} />
                      <Route path="notification/mail" element={<NotificationSettings />} />
                      <Route path="mail-view" element={<MailViewOptions />} />
                      <Route path="compose" element={<ComposeSettings />} />
                      <Route path="compose-customization" element={<ComposeSettings />} />
                      <Route path="offline" element={<AccountSettings />} />
                      <Route path="conversation" element={<AccountSettings />} />
                      <Route path="folders" element={<FoldersSettings />} />
                      <Route path="labels" element={<LabelsSettings />} />
                      <Route path="filters" element={<FiltersSettings />} />
                      <Route path="archive-policy" element={<AccountSettings />} />
                      <Route path="hotkeys" element={<KeyboardShortcuts />} />
                      <Route path="out-of-office" element={<OutOfOfficeSettings />} />
                      <Route path="sharing-delegation" element={<AccountSettings />} />
                      <Route path="mail-accounts" element={<MailAccountsSettings />} />
                      <Route path="signature" element={<SignatureSettings />} />
                      <Route path="app-passwords" element={<AppPasswordsSettings />} />
                      <Route path="anti-spam" element={<AntiSpamSettings />} />
                      <Route path="import-export" element={<ImportExportSettings />} />
                      <Route path="groups" element={<AccountSettings />} />
                      <Route path="shared-mailbox" element={<AccountSettings />} />
                      <Route path="account" element={<AccountSettings />} />
                    </Route>
                  </Routes>
                </>
              </ProtectedRoute>
            }
          />
        )}

        {/* 404 */}
        <Route path="*" element={<Navigate to={isHelpdeskMode() ? '/support/dashboard' : '/'} replace />} />

        {/* Helpdesk access from normal webmail mode — full staff interface */}
        {!isHelpdeskMode() && (
          <Route
            path="/helpdesk"
            element={
              <ProtectedRoute>
                <HelpdeskLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/helpdesk/dashboard" replace />} />
            <Route path="dashboard" element={<HelpdeskDashboard />} />
            <Route path="tickets" element={<HelpdeskTickets />} />
            <Route path="tickets/:id" element={<HelpdeskTicketDetail />} />
            <Route path="queue" element={<HelpdeskAgentQueue />} />
            <Route path="customers" element={<HelpdeskCustomers />} />
            <Route path="knowledge" element={<HelpdeskKnowledgeBase />} />
            <Route path="reports" element={<HelpdeskReports />} />
            <Route path="settings" element={<HelpdeskSettings />} />
            <Route path="*" element={<Navigate to="/helpdesk/dashboard" replace />} />
          </Route>
        )}
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  const { actualMode, accentColor, selectedPalette, setMode, setAccentColor } = useThemeStore();
  const { isAuthenticated } = useAuthStore();
  const { settings, fetchSettings } = useSettingsStore();
  
  // DEBUG: Log localStorage state on mount
  useEffect(() => {
    console.log('=== APP MOUNT - CHECKING LOCALSTORAGE ===');
    const stored = localStorage.getItem('zmail-compose-tabs');
    console.log('[App] localStorage raw value:', stored);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        console.log('[App] localStorage parsed:', parsed);
      } catch (e) {
        console.error('[App] Failed to parse localStorage:', e);
      }
    } else {
      console.warn('[App] No zmail-compose-tabs in localStorage!');
    }
    
    // Also log current store state
    const storeState = useComposeTabsStore.getState();
    console.log('[App] Current store state:', {
      chatOpen: storeState.chatOpen,
      showingCompose: storeState.showingCompose,
      tabs: storeState.tabs.length
    });
  }, []);
  
  // Proactive token refresh — refresh 5 minutes before expiry
  useEffect(() => {
    if (!isAuthenticated) return;
    const scheduleRefresh = () => {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000;
        const now = Date.now();
        // Refresh 5 minutes before expiry, minimum 30 seconds from now
        const refreshIn = Math.max(expiresAt - now - 5 * 60 * 1000, 30000);
        return setTimeout(async () => {
          // H-06: refresh token is in the httpOnly cookie — sent automatically with withCredentials.
          try {
            const { default: axios } = await import('axios');
            const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
            const res = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
            const data = res.data?.data || res.data;
            if (data.accessToken) {
              localStorage.setItem('accessToken', data.accessToken);
              useAuthStore.setState({ token: data.accessToken, isAuthenticated: true });
            }
          } catch { /* interceptor will handle on next API call */ }
          // Re-schedule after refresh
          const tid = scheduleRefresh();
          if (tid) timerRef = tid;
        }, refreshIn);
      } catch { return undefined; }
    };
    let timerRef = scheduleRefresh();
    return () => { if (timerRef) clearTimeout(timerRef); };
  }, [isAuthenticated]);

  // Fetch settings and sync theme on app load when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🌙 App: User authenticated, fetching settings');
      fetchSettings();
      // Request desktop notification permission for background email alerts
      requestNotificationPermission();
    }
  }, [isAuthenticated, fetchSettings]);
  
  // Sync theme settings when settings are loaded
  useEffect(() => {
    const validModes: string[] = ['light', 'dark', 'system'];
    if (settings.theme) {
      console.log('🌙 App: Syncing theme from settings:', settings.theme);
      if (validModes.includes(settings.theme)) {
        setMode(settings.theme as 'light' | 'dark' | 'system');
      } else {
        // It's a palette ID (e.g. 'dark-charcoal') — use setSelectedPalette which derives actualMode
        useThemeStore.getState().setSelectedPalette(settings.theme);
      }
    }
    if (settings.accentColor) {
      console.log('🌙 App: Syncing accent color from settings:', settings.accentColor);
      setAccentColor(settings.accentColor as 'blue' | 'green' | 'yellow' | 'red' | 'pink' | 'gray' | 'teal' | 'purple' | 'orange');
    }
  }, [settings.theme, settings.accentColor, setMode, setAccentColor]);

  // Sync display density from settings to themeStore
  useEffect(() => {
    if (settings.displayDensity && ['comfortable', 'cozy', 'compact'].includes(settings.displayDensity)) {
      useThemeStore.getState().setDensityMode(settings.displayDensity as 'comfortable' | 'cozy' | 'compact');
    }
  }, [settings.displayDensity]);

  // Apply accessibility settings
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // High Contrast Mode
    if (settings.highContrast) {
      body.classList.add('high-contrast');
      root.style.setProperty('--app-contrast-filter', 'contrast(1.3)');
    } else {
      body.classList.remove('high-contrast');
      root.style.removeProperty('--app-contrast-filter');
    }

    // Reduce Motion
    if (settings.reduceMotion) {
      body.classList.add('reduce-motion');
      root.style.setProperty('--app-transition-duration', '0ms');
    } else {
      body.classList.remove('reduce-motion');
      root.style.removeProperty('--app-transition-duration');
    }

    // Screen Reader Optimization
    if (settings.screenReaderOptimized) {
      body.setAttribute('aria-live', 'polite');
      body.classList.add('screen-reader-optimized');
    } else {
      body.removeAttribute('aria-live');
      body.classList.remove('screen-reader-optimized');
    }
  }, [settings.highContrast, settings.reduceMotion, settings.screenReaderOptimized]);

  // Apply typography settings via CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    if (settings.fontFamily) {
      const fontMap: Record<string, string> = {
        'Inter': '"Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
        'Roboto': '"Roboto", "Helvetica Neue", Arial, sans-serif',
        'Open Sans': '"Open Sans", "Helvetica Neue", Arial, sans-serif',
        'Lato': '"Lato", "Helvetica Neue", Arial, sans-serif',
        'Poppins': '"Poppins", "Helvetica Neue", Arial, sans-serif',
        'Source Sans Pro': '"Source Sans Pro", "Helvetica Neue", Arial, sans-serif',
        'Nunito': '"Nunito", "Helvetica Neue", Arial, sans-serif',
        'Raleway': '"Raleway", "Helvetica Neue", Arial, sans-serif',
      };
      const resolved = fontMap[settings.fontFamily] || settings.fontFamily;
      root.style.setProperty('--app-font-family', resolved);
      document.body.style.fontFamily = resolved;
    }
    if (settings.fontSize && settings.fontSize >= 13) {
      root.style.setProperty('--app-font-size', `${settings.fontSize}px`);
      document.documentElement.style.fontSize = `${settings.fontSize}px`;
    }
    if (settings.lineSpacing) {
      root.style.setProperty('--app-line-height', String(settings.lineSpacing));
    }
  }, [settings.fontFamily, settings.fontSize, settings.lineSpacing]);
  
  // Memoize theme creation to avoid unnecessary re-renders
  const theme = useMemo(() => {
    console.log('🌙 App creating theme - actualMode:', actualMode, 'accentColor:', accentColor, 'selectedPalette:', selectedPalette);
    const palette = themePalettes.find(p => p.id === selectedPalette);
    const paletteColors = palette ? { sidebar: palette.sidebar, content: palette.content } : undefined;
    return createAppTheme(actualMode, accentColor, paletteColors);
  }, [actualMode, accentColor, selectedPalette]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrandingProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <AppContent />
          </Suspense>
        </BrowserRouter>
      </BrandingProvider>
    </ThemeProvider>
  );
};

export default App;
